/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { BidRequestEventNotification, BidRequestInputApprovalStatus, BidRequestProjectTeam, } from '../models';
import { listToString } from '../utils';
const approvalToBidRequestStepMap = {
    txm_approval: {
        [BidRequestInputApprovalStatus.APPROVED]: '/projects',
        [BidRequestInputApprovalStatus.CHANGES_REQUESTED]: '/projects',
        [BidRequestInputApprovalStatus.NOT_REVIEWED]: '/projects',
    },
    dev_approval: {
        [BidRequestInputApprovalStatus.APPROVED]: '/projects',
        [BidRequestInputApprovalStatus.CHANGES_REQUESTED]: '/projects',
        [BidRequestInputApprovalStatus.NOT_REVIEWED]: '/projects',
    },
    eng_approval: {
        [BidRequestInputApprovalStatus.APPROVED]: '/eng-inputs',
        [BidRequestInputApprovalStatus.CHANGES_REQUESTED]: '/eng-inputs',
        [BidRequestInputApprovalStatus.NOT_REVIEWED]: '/eng-inputs',
    },
    procurement_approval: {
        [BidRequestInputApprovalStatus.APPROVED]: '/procurement-inputs',
        [BidRequestInputApprovalStatus.CHANGES_REQUESTED]: '/procurement-inputs',
        [BidRequestInputApprovalStatus.NOT_REVIEWED]: '/procurement-inputs',
    },
    finance_approval: {
        [BidRequestInputApprovalStatus.APPROVED]: '/scenarios',
        [BidRequestInputApprovalStatus.CHANGES_REQUESTED]: '/bid-matrix',
        [BidRequestInputApprovalStatus.NOT_REVIEWED]: '/bid-matrix',
    },
};
export const formatBidRequestNotification = (notification, projectNames) => {
    const { meta } = notification;
    const { bid_request } = meta;
    if (!bid_request || !projectNames) {
        throw new Error('Bid request & project ID must be defined');
    }
    const { id, version, event, role, targeted_message } = bid_request;
    let message;
    let endpoint = '/bid-matrix';
    let customMsg = targeted_message;
    let possessive = false;
    const subject = `${bid_request.name}${version !== undefined ? ` v${version}` : ''}`;
    if (event === BidRequestEventNotification.INPUTS_REVIEWED && bid_request.approvals) {
        let action;
        const { approvals, status } = bid_request.approvals;
        const [firstApproval] = approvals;
        if (!firstApproval) {
            throw new Error('Approval message not defined for empty approval list');
        }
        endpoint = approvalToBidRequestStepMap[firstApproval][status];
        if (status === BidRequestInputApprovalStatus.APPROVED) {
            const approvalLabels = approvals.map((approval) => approval.split('_')[0]);
            action = `gave ${listToString(approvalLabels)} approval${approvalLabels.length > 1 ? 's' : ''}`;
        }
        else if (status === BidRequestInputApprovalStatus.CHANGES_REQUESTED) {
            action = 'requested changes from you';
        }
        else {
            throw new Error('Action message not defined for approval bid request notification');
        }
        message = `${action} on inputs for a bid request`;
    }
    else if (bid_request.num_pricing_scenarios) {
        message = `uploaded ${bid_request.num_pricing_scenarios} pricing scenarios for a bid request to bnCRM`;
        endpoint = '/scenarios';
    }
    else if (event === BidRequestEventNotification.CREATED) {
        message = 'created a new bid request';
    }
    else if (event === BidRequestEventNotification.ADD_ENG_CONFIGS) {
        message = 'added eng configs on a bid request';
        endpoint = '/eng-inputs';
    }
    else if (event === BidRequestEventNotification.SIMULATE_ENG_SCENARIOS) {
        possessive = true;
        if (bid_request.missing_idxs) {
            message = `eng simulations completed, with ${meta.num_successes} success${(meta.num_successes || 2) > 1 ? 'es' : ''} and ${meta.num_of_errors} failing, on a bid request`;
            endpoint = '/eng-inputs';
            customMsg = `You will need to re-run once the issues with ${(meta.num_successes ?? 0) > 0
                ? `config entr${bid_request.missing_idxs.length > 1 ? 'ies' : 'y'} #${bid_request.missing_idxs.join(', ')}`
                : 'all config entries'} are fixed.`;
        }
        else {
            message = `eng simulations completed (${meta.num_successes} successes) on a bid request`;
            endpoint = '/eng-inputs';
            customMsg = 'Click to add eng inputs.';
        }
    }
    else if (event === BidRequestEventNotification.ADD_ENG_INPUTS) {
        message = 'added eng inputs for a bid request';
        endpoint = '/eng-inputs';
    }
    else if (event === BidRequestEventNotification.COMPLETED) {
        message = 'completed a bid request';
        endpoint = '/scenarios';
    }
    else if (event === BidRequestEventNotification.NEW_VERSION) {
        message = 'edited development inputs of a bid request';
    }
    else if (event === BidRequestEventNotification.NEW_VERSION_ORIG) {
        message = 'edited origination inputs of a bid request';
    }
    else if (event === BidRequestEventNotification.REQUEST_SCENARIO_VARIANTS) {
        message = 'has requested variations of bid matrix scenarios on a bid request';
        endpoint = '/scenarios';
    }
    else {
        throw new Error('Message not defined for approval bid request notification');
    }
    const versionSearch = version !== undefined ? `?version=${version}` : '';
    const link = `/bid-requests/${id}${endpoint}${versionSearch}`;
    const roleContext = role
        ? ` (you are tagged as ${BidRequestProjectTeam[role]} for ${listToString(projectNames)} project${projectNames?.length > 1 ? 's' : ''}).`
        : undefined;
    const messagePostFix = customMsg ? `. ${customMsg}` : (roleContext ?? '.');
    message = `${message} for ${bid_request.opportunity_name} opportunity${messagePostFix}`;
    return { message, link, subject, possessive };
};
//# sourceMappingURL=notifications.js.map